import type { PinnedReviewQueueEntry } from "#use-pinned-review-queue-query";

/**
 * Put `userCardId` into the hardcoded queue at `slotIndex` of the Admin panel editor's list: the unspent
 * pinned entries (`pinnedEntries`, in order), then the computed queue (`upcomingUserCardIds`).
 *
 * The computed order is not stored, so slotting a card in between two computed cards pins every
 * computed card above the slot as well; the slot then keeps its place however the computed queue
 * reorders later. A card already pinned moves to the slot rather than appearing twice (one review
 * would spend both entries).
 */
export function slotCardIntoPinnedReviewQueue({
  now,
  pinnedEntries,
  slotIndex,
  upcomingUserCardIds,
  userCardId,
}: {
  now: Date;
  pinnedEntries: readonly PinnedReviewQueueEntry[];
  slotIndex: number;
  upcomingUserCardIds: readonly string[];
  userCardId: string;
}): PinnedReviewQueueEntry[] {
  const pinnedAt = now.toISOString();
  const promoted = upcomingUserCardIds
    .slice(0, Math.max(0, slotIndex - pinnedEntries.length))
    .map((id) => ({ pinnedAt, userCardId: id }));
  const head = [...pinnedEntries, ...promoted];
  const insertAt = Math.min(Math.max(0, slotIndex), head.length);
  return [
    ...head.slice(0, insertAt).filter((entry) => entry.userCardId !== userCardId),
    { pinnedAt, userCardId },
    ...head.slice(insertAt).filter((entry) => entry.userCardId !== userCardId),
  ];
}

/** Swap the entry at `index` with its neighbour `offset` (−1 up, +1 down) places away. */
export function movePinnedReviewQueueEntry(
  entries: readonly PinnedReviewQueueEntry[],
  index: number,
  offset: -1 | 1,
): PinnedReviewQueueEntry[] {
  const target = index + offset;
  const moving = entries[index];
  const displaced = entries[target];
  if (moving === undefined || displaced === undefined) {
    return [...entries];
  }
  const next = [...entries];
  next[target] = moving;
  next[index] = displaced;
  return next;
}

export function removePinnedReviewQueueEntry(
  entries: readonly PinnedReviewQueueEntry[],
  index: number,
): PinnedReviewQueueEntry[] {
  return entries.filter((_, entryIndex) => entryIndex !== index);
}
